// @flow

/**
 * Module dependencies.
 */

import { Col, Row } from 'client/components/core/grid';
import { colors, media, units } from 'client/styles';
import Icon from 'client/components/core/icon';
import Layout from 'client/components/core/layout';
import RawHtml from 'react-raw-html';
import React from 'react';
import Text from 'client/components/core/type';
import styled from 'styled-components';

/**
 * `Item` type.
 */

type Item = {
  icon: string,
  text: string,
  title: string
};

/**
 * `Props` type.
 */

type Props = {
  leftItem: Item,
  rightItem: Item
};

/**
 * Styles.
 */

const styles = {
  leftIcon: `
    svg {
      width: 135%;
      margin-left: -8%;
    }
  `,
  rightIcon: `
    svg {
      width: 130%;
      margin-left: -20%;
    }
  `
};

/**
 * `Illustration` component.
 */

const Illustration = styled.div`
  ${media.min.xs`display: none;`}
  ${media.min.md`display: block;`}
`;

/**
 * `SideIllustration` component.
 */

const SideIllustration = styled.div`
  display: none;

  ${media.min.sm`display: block;`}
  ${media.min.md`display: none;`}
`;

/**
 * Breakpoints to bootstrap columns.
 */

const columns = {
  md: 12,
  sm: 6,
  xs: 12
};

/**
 * Render title.
 */

const renderTitle = (title: string) => (
  <Text.H2 color={colors.brandPrimary}>
    {title}
  </Text.H2>
);

/**
 * Render text.
 */

const renderText = (text: string) => {
  return (
    <Text.H4 weight={300}>
      <RawHtml.span>
        {text}
      </RawHtml.span>
    </Text.H4>
  );
};

/**
 * Render icon.
 */

function renderIcon(icon: string, styles?: string) {
  return (
    <Layout
      paddingBottom={units(2)}
      paddingTop={units(3)}
    >
      <Icon
        icon={icon}
        size={'100%'}
        styles={styles}
      />
    </Layout>
  );
}

/**
 * `Account` component.
 */

function Account(props: Props): React$Element<*> {
  const { leftItem, rightItem } = props;

  return (
    <Layout overflow={'hidden'}>
      <Row>
        <Col
          md={4}
          xs={10}
          xsOffset={1}
        >
          <Layout
            paddingBottom={units(8)}
            paddingBottomMd={units(0)}
          >
            <Row>
              <Col {...columns}>
                <Row>
                  <Col xs={12}>
                    {renderTitle(leftItem.title)}

                    <Illustration>
                      {renderIcon(leftItem.icon)}
                    </Illustration>
                  </Col>

                  <Col xs={12}>
                    {renderText(leftItem.text)}
                  </Col>
                </Row>
              </Col>

              <Col {...columns}>
                <SideIllustration>
                  {renderIcon(leftItem.icon, styles.leftIcon)}
                </SideIllustration>
              </Col>
            </Row>
          </Layout>
        </Col>

        <Col
          md={4}
          xs={10}
          xsOffset={1}
        >
          <Row>
            <Col {...columns}>
              <SideIllustration>
                {renderIcon(rightItem.icon, styles.rightIcon)}
              </SideIllustration>
            </Col>

            <Col {...columns}>
              {renderTitle(rightItem.title)}

              <Illustration>
                {renderIcon(rightItem.icon)}
              </Illustration>
            </Col>

            <Col {...columns}>
              {renderText(rightItem.text)}
            </Col>
          </Row>
        </Col>
      </Row>
    </Layout>
  );
}

/**
 * Export `Account`.
 */

export default Account;



// WEBPACK FOOTER //
// ../src/client/components/how-it-works/account-and-deal.js